import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";

export default function ResourceTable({
  title, testid, columns, fields = [], fetchList, createItem, updateItem, deleteItem,
  rowActions, canCreate = true, createLabel = "Ajouter", emptyText = "Aucun élément",
}) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [insertOpts, setInsertOpts] = useState({});

  const load = async () => {
    try { setRows(await fetchList()); }
    catch (e) { toast.error(apiError(e)); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loadInserts = async () => {
    const out = {};
    for (const f of fields) { if (f.inserts) { try { out[f.name] = await f.inserts(); } catch { out[f.name] = []; } } }
    setInsertOpts(out);
  };

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => (init[f.name] = f.default ?? (f.type === "number" ? 0 : "")));
    setForm(init); setEditing(null); setOpen(true); loadInserts();
  };
  const openEdit = (row) => {
    const init = {};
    fields.forEach((f) => (init[f.name] = row[f.name] ?? ""));
    setForm(init); setEditing(row); setOpen(true); loadInserts();
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0; });
      if (editing) await updateItem(editing.id, payload);
      else await createItem(payload);
      toast.success(editing ? "Modifié" : "Ajouté");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const remove = async (row) => {
    if (!window.confirm("Confirmer la suppression ?")) return;
    try { await deleteItem(row.id); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-bold text-white">{title}</h2>
        {canCreate && createItem && (
          <Button onClick={openCreate} data-testid={`${testid}-add-btn`} className="vp-accent-bg text-black hover:opacity-90 font-medium">
            <Plus size={16} className="mr-1" /> {createLabel}
          </Button>
        )}
      </div>

      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead key={c.key} className="text-slate-400 text-xs uppercase tracking-wider">{c.label}</TableHead>
                ))}
                {(updateItem || deleteItem || rowActions) && <TableHead className="text-right text-slate-400">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-slate-500">
                  <Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…
                </TableCell></TableRow>
              )}
              {rows && rows.length === 0 && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-slate-500">{emptyText}</TableCell></TableRow>
              )}
              {rows && rows.map((row) => (
                <TableRow key={row.id} data-testid={`${testid}-row`} className="border-white/5 hover:bg-white/[0.03]">
                  {columns.map((c) => (
                    <TableCell key={c.key} className="text-slate-200 text-sm">
                      {c.render ? c.render(row) : (row[c.key] ?? "—")}
                    </TableCell>
                  ))}
                  {(updateItem || deleteItem || rowActions) && (
                    <TableCell className="text-right whitespace-nowrap">
                      {rowActions && rowActions(row, load)}
                      {updateItem && (
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)} className="text-slate-400 hover:text-white h-8 w-8" data-testid={`${testid}-edit-btn`}>
                          <Pencil size={15} />
                        </Button>
                      )}
                      {deleteItem && (
                        <Button size="icon" variant="ghost" onClick={() => remove(row)} className="text-slate-400 hover:text-red-400 h-8 w-8" data-testid={`${testid}-delete-btn`}>
                          <Trash2 size={15} />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="vp-card border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Modifier" : createLabel}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.name}>
                <Label className="text-slate-300 text-sm mb-1.5 block">{f.label}</Label>
                {f.inserts && insertOpts[f.name]?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {insertOpts[f.name].map((o, i) => (
                      <button key={i} type="button" onClick={() => setForm({ ...form, [f.name]: o.value })}
                        className="text-xs px-2 py-1 rounded bg-white/10 text-slate-300 hover:bg-white/20" data-testid={`${testid}-insert-${f.name}`}>+ {o.label}</button>
                    ))}
                  </div>
                )}
                {f.type === "textarea" ? (
                  <Textarea value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="bg-black/30 border-white/10 text-white" data-testid={`${testid}-field-${f.name}`} />
                ) : f.type === "select" ? (
                  <Select value={String(form[f.name] ?? "")} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white" data-testid={`${testid}-field-${f.name}`}><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="vp-card border-white/10 text-white">
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="bg-black/30 border-white/10 text-white" data-testid={`${testid}-field-${f.name}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button onClick={save} disabled={saving} className="vp-accent-bg text-black font-medium" data-testid={`${testid}-save-btn`}>
              {saving && <Loader2 className="animate-spin mr-2" size={15} />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
